import React, { useCallback } from 'react';
import { getIn } from 'formik';

import { TextField } from '../../../text-field';
import { NumberFormatCustom } from './number-format';
import { FieldElementPropTypes } from '../../propTypes';
import { buildTooltipAdornment } from '../tooltip-adornment';

const Numeric = ({
  field: { id, name, label, mode, format, value, onChange: customOnChange, hideErrorMessage, ...props },
  ctx: { handleChange, touched, errors, classes, setFieldTouched, setFieldValue }
}) => {
  const onChange = useCallback(
    (e) => {
      const newValue = e.target.value;
      // Skip no-op updates to prevent infinite render loops (react-number-format
      // fires onValueChange on re-render even when the value hasn't changed). Treat all
      // empty representations (null/undefined/'') as equal so an empty re-fire can't rewrite
      // a parent-set `null` to `undefined` — `undefined` is dropped by JSON.stringify of the
      // GraphQL variables, silently removing a deliberately-cleared field from the save payload
      // and defeating "remove coverage" clears (#34985).
      const isEmpty = (v) => v === null || v === undefined || v === '';
      if (newValue === value || (isEmpty(newValue) && isEmpty(value))) {
        return;
      }
      handleChange(e);
      // Write to the Formik field `name`, not the DOM `id`, so callers can pass a unique DOM `id`
      // (duplicate-id focus bug, #32617) without diverting the value off the flat Formik path.
      setFieldValue(name || id, newValue);
      setFieldTouched(name || id, true, false);
      if (customOnChange) {
        customOnChange(newValue);
      }
    },
    [handleChange, setFieldTouched, id, name, value, customOnChange, setFieldValue]
  );
  const { inputProps, InputProps, noMarginRight, tooltipText, tooltipLabel, ...elemProps } = props || {};
  const tooltipAdornment = buildTooltipAdornment(tooltipText, tooltipLabel);

  return (
    <TextField
      key={id}
      id={id}
      name={name || id}
      label={label}
      value={typeof value === 'number' ? value : undefined}
      onChange={onChange}
      error={getIn(touched, name || id) && !!getIn(errors, name || id)}
      helperText={hideErrorMessage ? undefined : getIn(touched, name || id) && getIn(errors, name || id)}
      mode={mode}
      fullWidth
      classes={classes[id]}
      style={noMarginRight ? undefined : { marginRight: 8 }}
      {...elemProps}
      InputProps={{
        inputComponent: NumberFormatCustom,
        inputProps: {
          format,
          id
        },
        name: name || id,
        ...(inputProps || {}),
        ...(tooltipAdornment ? { endAdornment: tooltipAdornment } : {}),
        ...(InputProps || {})
      }}
    />
  );
};

Numeric.propTypes = FieldElementPropTypes;

export default Numeric;
